import { useState, useEffect } from "react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

import { GamificationDashboard } from "../gamification/GamificationDashboard";

import { ActivityGoalsManager } from "./ActivityGoalsManager";
import { ActivityHeatMap } from "./ActivityHeatMap";
import { ActivityStreaksTracker } from "./ActivityStreaksTracker";
import { ActivityTimeline } from "./ActivityTimeline";



interface ActivityInsightsProps {
  workspaceId: string;
}

export const ActivityInsights = ({ workspaceId }: ActivityInsightsProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workspaceData, setWorkspaceData] = useState<any[]>([]);
  const [userData, setUserData] = useState<any[]>([]);

  useEffect(() => {
    const fetchActivityData = async () => {
      if (!user || !workspaceId) return;

      try {
        setLoading(true);

        // Fetch workspace activity (365 days)
        const { data: workspaceActivity, error: workspaceError } = await supabase.rpc(
          'get_workspace_activity_heatmap',
          {
            p_workspace_id: workspaceId,
            p_days_back: 365
          }
        );

        if (workspaceError) throw workspaceError;

        // Fetch user activity across all workspaces (365 days)
        const { data: userActivity, error: userError } = await supabase.rpc(
          'get_user_activity_heatmap',
          {
            p_user_id: user.id,
            p_days_back: 365
          }
        );

        if (userError) throw userError;

        setWorkspaceData(workspaceActivity || []);
        setUserData(userActivity || []);
      } catch (error) {
        console.error('Error fetching activity data:', error);
        toast.error('Failed to load activity data');
      } finally {
        setLoading(false);
      }
    };

    fetchActivityData();
  }, [user, workspaceId]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Goals and Streaks Skeletons */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="animate-content-reveal">
            <CardHeader className="space-y-2">
              <Skeleton className="h-6 w-32" speed="fast" />
              <Skeleton className="h-4 w-48" speed="fast" delay={50} />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-24" speed="fast" delay={i * 75} />
                      <Skeleton className="h-4 w-16" speed="fast" delay={i * 75 + 25} />
                    </div>
                    <Skeleton className="h-2 w-full" speed="fast" delay={i * 75 + 50} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="animate-content-reveal" style={{ animationDelay: '100ms' }}>
            <CardHeader className="space-y-2">
              <Skeleton className="h-6 w-32" speed="fast" />
              <Skeleton className="h-4 w-48" speed="fast" delay={50} />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="text-center p-3 rounded-lg border">
                    <Skeleton className="h-8 w-12 mx-auto mb-2" speed="fast" delay={i * 60} />
                    <Skeleton className="h-3 w-16 mx-auto" speed="fast" delay={i * 60 + 30} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gamification Skeleton */}
        <Card className="bg-linear-to-br from-primary/10 to-primary/5 animate-content-reveal" style={{ animationDelay: '200ms' }}>
          <CardHeader className="space-y-3">
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <Skeleton className="h-7 w-24" speed="slow" />
                <Skeleton className="h-4 w-32" speed="slow" delay={75} />
              </div>
              <Skeleton className="h-8 w-8 rounded-full" speed="slow" delay={50} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-28" speed="slow" delay={100} />
                <Skeleton className="h-4 w-20" speed="slow" delay={125} />
              </div>
              <Skeleton className="h-3 w-full" speed="slow" delay={150} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-4 border-t">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="text-center p-3 rounded-lg bg-background/50">
                  <Skeleton className="h-6 w-8 mx-auto mb-1" speed="slow" delay={i * 50} />
                  <Skeleton className="h-3 w-12 mx-auto" speed="slow" delay={i * 50 + 25} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Achievements Skeleton */}
        <Card className="animate-content-reveal" style={{ animationDelay: '300ms' }}>
          <CardHeader className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" delay={50} />
          </CardHeader>
          <CardContent className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg border" style={{ animationDelay: `${i * 100}ms` }}>
                <Skeleton className="h-12 w-12 rounded-full" delay={i * 80} />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" delay={i * 80 + 30} />
                  <Skeleton className="h-3 w-48" delay={i * 80 + 60} />
                </div>
                <Skeleton className="h-5 w-12" delay={i * 80 + 40} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Activity Tabs Skeleton */}
        <Card className="animate-content-reveal" style={{ animationDelay: '400ms' }}>
          <CardHeader>
            <Skeleton className="h-10 w-64" speed="slow" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" speed="slow" delay={50} />
              <Skeleton className="h-4 w-full max-w-md" speed="slow" delay={100} />
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 365 }).map((_, i) => (
                <Skeleton key={i} className="h-3 w-3" speed="slow" delay={Math.floor(i / 7) * 20} />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-content-reveal">
      <div className="grid gap-6 lg:grid-cols-2">
        <ActivityGoalsManager />
        <ActivityStreaksTracker />
      </div>

      <GamificationDashboard />

      <Tabs defaultValue="workspace" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="workspace">Workspace Activity</TabsTrigger>
          <TabsTrigger value="user">My Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="workspace" className="space-y-6 mt-6">
          <ActivityHeatMap
            data={workspaceData}
            title="Workspace Activity Heat Map"
            description="GitHub-style contribution graph showing workspace activity over the past year"
          />
          <ActivityTimeline
            data={workspaceData}
            title="Workspace Activity Timeline"
            description="Detailed breakdown of workspace activity trends"
            showProjects
            showBoards={false}
          />
        </TabsContent>

        <TabsContent value="user" className="space-y-6 mt-6">
          <ActivityHeatMap
            data={userData}
            title="Your Activity Heat Map"
            description="Your personal contribution graph across all workspaces"
          />
          <ActivityTimeline
            data={userData}
            title="Your Activity Timeline"
            description="Your personal activity trends and patterns"
            showProjects
            showBoards
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
